import React, { useState, useEffect, useRef } from 'react';
import { Event, PriceAdjustment } from './types';
import { format } from 'date-fns';
import { edgeFunctionUrl, EdgeFunctions } from '../../../lib/edgeFunctions';
import { supabase } from '../../../lib/supabaseClient';
import { loadStripeTerminal } from '@stripe/terminal-js';
import TipModal from './TipModal';
import FormStatusBadge from '../../../components/forms/FormStatusBadge';
import PriceAdjustmentSection from './PriceAdjustmentSection';
import { useAuth } from '../../../contexts/auth/AuthContext';
import SignatureCanvas from '../../../components/forms/SignatureCanvas';

interface AppointmentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: Event | null;
  onEdit?: () => void;
  onDelete?: (eventId: string | number) => Promise<void>;
  onPriceAdjustment?: (appointmentId: string, adjustedTotal: number, priceAdjustment: PriceAdjustment) => void;
  maxDiscountPercent?: number; // For crew: 80, admin: 100 (default)
  onPaymentComplete?: () => void;
}

const AppointmentDetailsModal: React.FC<AppointmentDetailsModalProps> = ({
  isOpen,
  onClose,
  appointment,
  onEdit,
  onDelete,
  onPriceAdjustment,
  maxDiscountPercent = 100,
  onPaymentComplete,
}) => {
  const { isAdmin, isCrew, userProfile } = useAuth();
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isApplyingAdjustment, setIsApplyingAdjustment] = useState(false);

  // Terminal payment states
  const [isProcessingTerminal, setIsProcessingTerminal] = useState(false);
  const [terminalError, setTerminalError] = useState<string | null>(null);
  const [terminalStatus, setTerminalStatus] = useState<string>('');
  const [canCancelPayment, setCanCancelPayment] = useState(false);
  const terminalRef = useRef<any>(null);
  const discoveredReadersRef = useRef<any[]>([]);
  const paymentIntentRef = useRef<any>(null);

  // Tip states
  const [isTipModalOpen, setIsTipModalOpen] = useState(false);
  const [existingTip, setExistingTip] = useState<{ amount: number; tip_percentage: number | null } | null>(null);
  const [isFetchingTip, setIsFetchingTip] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [paymentSource, setPaymentSource] = useState<string | null>(null);

  // Payment discount states (calculated from existing payment columns)
  const [paymentDiscountInfo, setPaymentDiscountInfo] = useState<{
    originalAmount: number | null;      // Price before any discounts
    actualPaidAmount: number | null;    // Final amount charged/due
    memberDiscountAmount: number | null;
    memberDiscountPercent: number | null;
    payNowDiscountAmount: number | null; // Calculated: original - actual - memberDiscount
    creditSavings: number | null;       // Amount covered by service credits
    paymentType: string | null;         // 'card', 'wallet', 'credit', 'pay_at_spa'
  } | null>(null);

  // Client wallet balance and saved card for tip payment
  const [clientWalletBalance, setClientWalletBalance] = useState<number | null>(null);
  const [clientHasSavedCard, setClientHasSavedCard] = useState<boolean>(false);

  // Client ID for wallet payment on behalf of client
  const [clientId, setClientId] = useState<string | null>(null);

  // Wallet payment states
  const [isProcessingWalletPayment, setIsProcessingWalletPayment] = useState(false);
  const [walletPaymentError, setWalletPaymentError] = useState<string | null>(null);

  // Consent form states
  const [consentFormComplete, setConsentFormComplete] = useState<boolean | null>(null);
  const [consentFormToken, setConsentFormToken] = useState<string | null>(null);

  // Signature collection states
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const [existingSignatureUrl, setExistingSignatureUrl] = useState<string | null>(null);
  const [existingSignedAt, setExistingSignedAt] = useState<string | null>(null);
  const [paymentRecordId, setPaymentRecordId] = useState<string | null>(null);
  const signatureSectionRef = useRef<HTMLDivElement>(null);

  // Local payment status tracking (to keep modal open after payment)
  const [localPaymentStatus, setLocalPaymentStatus] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Cash payment states
  const [isProcessingCashPayment, setIsProcessingCashPayment] = useState(false);
  const [cashPaymentError, setCashPaymentError] = useState<string | null>(null);

  // Member credit states
  const [memberCreditAvailable, setMemberCreditAvailable] = useState<{
    serviceId: string;
    serviceName: string;
    quantity: number;
    creditValue: number;
  } | null>(null);
  const [isApplyingCredit, setIsApplyingCredit] = useState(false);
  const [creditError, setCreditError] = useState<string | null>(null);

  // Crew notes editing state
  const [isEditingCrewNotes, setIsEditingCrewNotes] = useState(false);
  const [crewNotesValue, setCrewNotesValue] = useState('');
  const [isSavingCrewNotes, setIsSavingCrewNotes] = useState(false);

  // Initialize Stripe Terminal
  useEffect(() => {
    const initializeTerminal = async () => {
      if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
        console.error('Stripe publishable key not found');
        return;
      }

      try {
        const StripeTerminal = await loadStripeTerminal();

        if (!StripeTerminal) {
          console.error('Failed to load Stripe Terminal');
          return;
        }

        terminalRef.current = StripeTerminal.create({
          onFetchConnectionToken: async () => {
            // Fetch connection token from our edge function
            const { data: { session } } = await supabase.auth.getSession();

            const response = await fetch(edgeFunctionUrl(EdgeFunctions.CREATE_CONNECTION_TOKEN), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token || ''}`,
                'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
              },
            });

            const data = await response.json();

            if (!response.ok) {
              throw new Error(data.error || 'Failed to fetch connection token');
            }

            return data.secret;
          },
          onUnexpectedReaderDisconnect: () => {
            setTerminalStatus('Reader disconnected');
          },
        });
      } catch (error) {
        // Terminal initialization failed
      }
    };

    if (isOpen) {
      initializeTerminal();
    }

    return () => {
      // Cleanup: safely disconnect reader when modal closes
      const cleanup = async () => {
        if (terminalRef.current) {
          try {
            // Only disconnect if we're actually connected
            const status = terminalRef.current.getConnectionStatus();
            if (status === 'connected') {
              await terminalRef.current.disconnectReader();
            }
          } catch (err) {
            // Silent cleanup
          }
        }
      };
      cleanup();
    };
  }, [isOpen]);

  // Fetch existing tip and payment method when appointment changes
  useEffect(() => {
    const fetchTipAndPaymentInfo = async () => {
      if (!appointment || !appointment.id) {
        setExistingTip(null);
        setPaymentMethodId(null);
        setPaymentSource(null);
        setPaymentDiscountInfo(null);
        setClientWalletBalance(null);
        setClientId(null);
        setMemberCreditAvailable(null);
        setPaymentRecordId(null);
        setExistingSignatureUrl(null);
        setExistingSignedAt(null);
        setLocalPaymentStatus(null);
        setRefreshCounter(0);
        return;
      }

      setIsFetchingTip(true);
      try {
        // Fetch tip - use maybeSingle() to avoid errors when no tip exists
        const { data: tipData } = await supabase
          .from('tips')
          .select('amount, tip_percentage')
          .eq('appointment_id', appointment.id)
          .eq('status', 'succeeded')
          .maybeSingle();

        setExistingTip(tipData);

        // Fetch payment info (only columns that exist in the database)
        // Include both 'succeeded' and 'pending' status (for pay_at_spa payments)
        // Also filter out 'canceled' records (stale Pay Now payments when user switched to Pay at Store)
        const { data: paymentData } = await supabase
          .from('payments')
          .select('id, payment_method_id, payment_source, amount, original_amount, member_discount_amount, member_discount_percent, credit_savings, payment_type, status, signature_url, signed_at')
          .eq('appointment_id', appointment.id)
          .in('status', ['succeeded', 'pending'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // Detect stale pending card payments: if the user selected "Pay Now" (creating a pending
        // card payment) then switched to "Pay at Store", the pending card record is stale.
        // The appointment's paymentStatus will be 'pending_payment' for Pay at Store.
        const isStaleCardPayment = paymentData
          && paymentData.status === 'pending'
          && paymentData.payment_type === 'card'
          && appointment.paymentStatus === 'pending_payment';

        const effectivePaymentData = isStaleCardPayment ? null : paymentData;

        setPaymentMethodId(effectivePaymentData?.payment_method_id || null);
        setPaymentSource(effectivePaymentData?.payment_source || null);
        setPaymentRecordId(effectivePaymentData?.id || null);
        setExistingSignatureUrl(effectivePaymentData?.signature_url || null);
        setExistingSignedAt(effectivePaymentData?.signed_at || null);

        // Set discount info if payment exists
        if (effectivePaymentData) {
          const originalAmount = paymentData.original_amount ?? null;
          const actualPaidAmount = paymentData.amount ?? null;
          const memberDiscountAmount = paymentData.member_discount_amount ?? 0;
          const creditSavings = paymentData.credit_savings ?? 0;

          // Calculate pay-now discount: original - actual - memberDiscount - creditSavings
          // This is because: actualPaid = original - memberDiscount - payNowDiscount - creditSavings
          let payNowDiscountAmount: number | null = null;
          if (originalAmount !== null && actualPaidAmount !== null) {
            const calculatedDiscount = originalAmount - actualPaidAmount - memberDiscountAmount - creditSavings;
            // Only set if there's actually a discount (> 0)
            payNowDiscountAmount = calculatedDiscount > 0.01 ? calculatedDiscount : null;
          }

          setPaymentDiscountInfo({
            originalAmount,
            actualPaidAmount,
            memberDiscountAmount: memberDiscountAmount > 0 ? memberDiscountAmount : null,
            memberDiscountPercent: paymentData.member_discount_percent ?? null,
            payNowDiscountAmount,
            creditSavings: creditSavings > 0 ? creditSavings : null,
            paymentType: paymentData.payment_type ?? null,
          });
        } else {
          setPaymentDiscountInfo(null);
        }

        // Fetch client's wallet balance and saved card info for tip payment options
        const { data: appointmentData } = await supabase
          .from('appointments')
          .select('client_id')
          .eq('id', appointment.id)
          .single();

        if (appointmentData?.client_id) {
          setClientId(appointmentData.client_id);
          const { data: clientProfile } = await supabase
            .from('user_profiles')
            .select('wallet_balance, stripe_customer_id, membership_status')
            .eq('id', appointmentData.client_id)
            .single();

          setClientWalletBalance(clientProfile?.wallet_balance || 0);
          setClientHasSavedCard(!!clientProfile?.stripe_customer_id);

          // Check for available member credits if client is an active member
          if (clientProfile?.membership_status === 'active' && appointment.services?.[0]?.id) {
            const { data: credits } = await supabase.rpc('get_member_credits', {
              p_user_id: appointmentData.client_id,
            });
            if (credits && credits.length > 0) {
              const serviceId = appointment.services[0].id;
              const matchingCredit = credits.find(
                (c: { service_id: string; quantity: number }) => c.service_id === serviceId && c.quantity > 0
              );
              if (matchingCredit) {
                setMemberCreditAvailable({
                  serviceId: matchingCredit.service_id,
                  serviceName: matchingCredit.service_name,
                  quantity: matchingCredit.quantity,
                  creditValue: appointment.services[0].price || 0,
                });
              } else {
                setMemberCreditAvailable(null);
              }
            } else {
              setMemberCreditAvailable(null);
            }
          } else {
            setMemberCreditAvailable(null);
          }
        } else {
          setClientId(null);
          setClientWalletBalance(null);
          setClientHasSavedCard(false);
          setMemberCreditAvailable(null);
        }
      } catch (err) {
        setExistingTip(null);
        setPaymentMethodId(null);
        setPaymentSource(null);
        setPaymentDiscountInfo(null);
        setClientId(null);
        setClientWalletBalance(null);
        setClientHasSavedCard(false);
        setMemberCreditAvailable(null);
        setPaymentRecordId(null);
        setExistingSignatureUrl(null);
        setExistingSignedAt(null);
      } finally {
        setIsFetchingTip(false);
      }
    };

    fetchTipAndPaymentInfo();
  }, [appointment?.id, refreshCounter]);

  // Fetch consent form status when appointment changes
  useEffect(() => {
    const fetchConsentFormStatus = async () => {
      if (!appointment || !appointment.clientEmail) {
        setConsentFormComplete(null);
        setConsentFormToken(null);
        return;
      }

      try {
        // Check if client has a consent form by email
        const { data: formData, error } = await supabase
          .from('facial_consent_forms')
          .select('form_token, signature_url')
          .eq('email', appointment.clientEmail)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error || !formData) {
          // No form exists
          setConsentFormComplete(false);
          setConsentFormToken(null);
        } else {
          // Form exists - check if signed
          setConsentFormComplete(!!formData.signature_url);
          setConsentFormToken(formData.form_token);
        }
      } catch (err) {
        setConsentFormComplete(null);
        setConsentFormToken(null);
      }
    };

    fetchConsentFormStatus();
  }, [appointment?.clientEmail]);

  // Auto-scroll to signature pad when it appears
  useEffect(() => {
    if (showSignaturePad && signatureSectionRef.current) {
      signatureSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showSignaturePad]);

  // Initialize crew notes when appointment changes
  useEffect(() => {
    if (appointment) {
      setCrewNotesValue(appointment.crewNotes || '');
      setIsEditingCrewNotes(false);
    }
  }, [appointment?.id]);

  // Handle saving crew notes
  const handleSaveCrewNotes = async () => {
    if (!appointment || !appointment.id) return;

    setIsSavingCrewNotes(true);
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ crew_notes: crewNotesValue.trim() || null })
        .eq('id', appointment.id);

      if (error) {
        console.error('Error saving crew notes:', error);
        alert('Failed to save notes. Please try again.');
        return;
      }

      setIsEditingCrewNotes(false);
      // Update local state
      if (appointment) {
        appointment.crewNotes = crewNotesValue.trim() || '';
      }
    } catch (err) {
      console.error('Error saving crew notes:', err);
      alert('Failed to save notes. Please try again.');
    } finally {
      setIsSavingCrewNotes(false);
    }
  };

  const handleCancelCrewNotesEdit = () => {
    setCrewNotesValue(appointment?.crewNotes || '');
    setIsEditingCrewNotes(false);
  };

  const effectivePaymentStatus = localPaymentStatus || appointment?.paymentStatus;

  if (!isOpen || !appointment || appointment.isBlockedTime) return null;

  const handleCreatePaymentLink = async () => {
    setIsCreatingPayment(true);
    setPaymentError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      // Determine redirect path based on current location (admin vs crew)
      const currentPath = window.location.pathname;
      const redirectPath = currentPath.includes('/crew/') ? '/crew/schedule' : '/admin/schedule';

      const response = await fetch(edgeFunctionUrl(EdgeFunctions.CREATE_CHECKOUT_SESSION), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          appointment_id: appointment.id,
          return_url: window.location.origin,
          redirect_path: redirectPath, // Tell edge function where to redirect after payment
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create payment link');
      }

      // Open the checkout URL in the same window (for tablet use)
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      setPaymentError(error.message || 'Failed to create payment link');
    } finally {
      setIsCreatingPayment(false);
    }
  };

  const handleCancelPayment = async () => {
    try {
      setTerminalStatus('Cancelling...');

      // Cancel the payment intent if it exists
      if (paymentIntentRef.current) {
        await terminalRef.current?.cancelCollectPaymentMethod();
      }

      // Disconnect reader
      if (terminalRef.current) {
        const status = terminalRef.current.getConnectionStatus();
        if (status === 'connected') {
          await terminalRef.current.disconnectReader();
        }
      }

      // Reset states
      setTerminalStatus('');
      setTerminalError(null);
      setIsProcessingTerminal(false);
      setCanCancelPayment(false);
      paymentIntentRef.current = null;

    } catch (error: any) {
      setTerminalError('Failed to cancel payment. Please try closing the modal.');
      setIsProcessingTerminal(false);
      setCanCancelPayment(false);
    }
  };

  const handleProcessOnReader = async () => {
    // Confirm client name and amount before collecting payment
    const paymentTotal = appointment.adjustedTotal ?? appointment.totalAmount ?? 0;
    const confirmed = window.confirm(
      `Collect payment for ${appointment.client || 'Unknown Client'}?\n\nAmount: $${paymentTotal.toFixed(2)}\n\nPlease verify this is the correct appointment before proceeding.`
    );
    if (!confirmed) return;

    setIsProcessingTerminal(true);
    setTerminalError(null);
    setTerminalStatus('Connecting to reader...');
    setCanCancelPayment(false);

    try {
      if (!terminalRef.current) {
        throw new Error('Terminal not initialized');
      }

      if (!process.env.REACT_APP_STRIPE_LOCATION_ID) {
        throw new Error('Stripe Location ID not configured. Please add REACT_APP_STRIPE_LOCATION_ID to your .env file');
      }

      // Step 1: Discover readers
      setTerminalStatus('Discovering readers...');

      // Use simulated reader in test mode for development
      const isTestMode = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.includes('test') || false;
      const discoverConfig = isTestMode
        ? { simulated: true }  // Simulated reader for testing
        : { location: process.env.REACT_APP_STRIPE_LOCATION_ID };  // Real reader for production

      const discoverResult = await terminalRef.current.discoverReaders(discoverConfig);

      if (discoverResult.error) {
        throw new Error(`Failed to discover readers: ${discoverResult.error.message}`);
      }

      if (discoverResult.discoveredReaders.length === 0) {
        const errorMsg = isTestMode
          ? 'No simulated readers found. This might be a Stripe SDK issue - try refreshing the page.'
          : 'No readers found. Make sure your S700 is online and registered.';
        throw new Error(errorMsg);
      }

      discoveredReadersRef.current = discoverResult.discoveredReaders;

      // Step 2: Connect to first available reader
      const readerLabel = discoveredReadersRef.current[0].label || 'reader';
      const readerType = isTestMode ? ' (simulated)' : '';
      setTerminalStatus(`Connecting to ${readerLabel}${readerType}...`);
      const connectResult = await terminalRef.current.connectReader(discoveredReadersRef.current[0]);

      if (connectResult.error) {
        throw new Error(`Failed to connect to reader: ${connectResult.error.message}`);
      }

      // Step 3: Create Terminal PaymentIntent via edge function
      setTerminalStatus('Creating payment intent...');
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(edgeFunctionUrl(EdgeFunctions.CREATE_TERMINAL_PAYMENT_INTENT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          appointment_id: appointment.id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create payment intent');
      }

      paymentIntentRef.current = data.clientSecret;

      // Step 4: Collect payment on reader
      setTerminalStatus('Present card on reader...');
      setCanCancelPayment(true); // Allow cancellation while waiting for card

      const collectResult = await terminalRef.current.collectPaymentMethod(data.clientSecret);

      if (collectResult.error) {
        // Check if it was manually cancelled
        if (collectResult.error.code === 'canceled') {
          throw new Error('Payment cancelled by user');
        }
        throw new Error(`Payment collection failed: ${collectResult.error.message}`);
      }

      setCanCancelPayment(false); // No longer cancellable once card is collected

      // Step 5: Process payment
      setTerminalStatus('Processing payment...');
      const processResult = await terminalRef.current.processPayment(collectResult.paymentIntent);

      if (processResult.error) {
        throw new Error(`Payment processing failed: ${processResult.error.message}`);
      }

      // Clear the reader display so S700 returns to idle screen
      try {
        await terminalRef.current.clearReaderDisplay();
      } catch (clearErr) {
        // Non-blocking: payment already succeeded
      }

      // Extract payment method ID for restaurant-style tips
      const paymentMethodId = processResult.paymentIntent.payment_method || null;

      // Extract tip from on-reader tipping
      const tipAmountCents = processResult.paymentIntent.amount_details?.tip?.amount || 0;
      const tipAmount = tipAmountCents / 100;

      if (tipAmount > 0) {
        const serviceTotal = appointment.adjustedTotal ?? appointment.totalAmount ?? 0;
        const tipPercentage = serviceTotal > 0
          ? Math.round((tipAmount / serviceTotal) * 100 * 100) / 100
          : null;
        setExistingTip({ amount: tipAmount, tip_percentage: tipPercentage });
      }

      // Step 6: Update appointment status to paid
      const { error: updateError } = await supabase
        .from('appointments')
        .update({
          payment_status: 'paid',
          payment_method_id: paymentMethodId,
        })
        .eq('id', appointment.id);

      if (updateError) {
        throw new Error('Payment successful but failed to update appointment');
      }

      // Update payment record with payment method and source
      const { error: paymentUpdateError } = await supabase
        .from('payments')
        .update({
          status: 'succeeded',
          payment_method_id: paymentMethodId,
          payment_source: 'terminal',
        })
        .eq('stripe_payment_intent_id', processResult.paymentIntent.id);

      if (paymentUpdateError) {
        // Don't throw - appointment is already marked paid
      }

      setTerminalStatus(tipAmount > 0
        ? `Payment successful! Tip: $${tipAmount.toFixed(2)} included ✓`
        : 'Payment successful! ✓');

      // Re-fetch payment record ID for signature association
      const { data: paymentRecord } = await supabase
        .from('payments')
        .select('id')
        .eq('stripe_payment_intent_id', processResult.paymentIntent.id)
        .single();

      if (paymentRecord) setPaymentRecordId(paymentRecord.id);

      // Transition to signature pad after brief success message
      setTimeout(() => {
        setTerminalStatus('');
        setShowSignaturePad(true);
      }, 1500);

    } catch (error: any) {
      setTerminalError(error.message || 'Failed to process payment on reader');
      setTerminalStatus('');

      // Disconnect reader on error
      try {
        if (terminalRef.current) {
          const status = terminalRef.current.getConnectionStatus();
          if (status === 'connected') {
            await terminalRef.current.disconnectReader();
          }
        }
      } catch (disconnectErr) {
        // Silent cleanup
      }
    } finally {
      setIsProcessingTerminal(false);
      setCanCancelPayment(false);
      paymentIntentRef.current = null;
    }
  };

  const handleWalletPayment = async () => {
    if (!appointment || !appointment.id || !clientId) return;

    setIsProcessingWalletPayment(true);
    setWalletPaymentError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const paymentAmount = appointment.adjustedTotal ?? appointment.totalAmount ?? 0;

      const response = await fetch(edgeFunctionUrl(EdgeFunctions.USE_WALLET_PAYMENT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          appointment_id: appointment.id,
          amount: paymentAmount,
          client_id: clientId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to process wallet payment');
      }

      // Re-fetch payment record for signature association
      const { data: paymentRecord } = await supabase
        .from('payments')
        .select('id')
        .eq('appointment_id', appointment.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (paymentRecord) setPaymentRecordId(paymentRecord.id);
      setShowSignaturePad(true);
    } catch (error: any) {
      setWalletPaymentError(error.message || 'Failed to process wallet payment');
    } finally {
      setIsProcessingWalletPayment(false);
    }
  };

  const handleCashPayment = async () => {
    if (!appointment || !appointment.id) return;

    setIsProcessingCashPayment(true);
    setCashPaymentError(null);

    try {
      const paymentAmount = appointment.adjustedTotal ?? appointment.totalAmount ?? 0;

      // Insert payment record
      const { data: paymentRecord, error: insertError } = await supabase
        .from('payments')
        .insert({
          appointment_id: appointment.id,
          amount: paymentAmount,
          original_amount: appointment.totalAmount ?? paymentAmount,
          payment_type: 'cash',
          status: 'succeeded',
          currency: 'usd',
        })
        .select('id')
        .single();

      if (insertError) throw new Error(insertError.message || 'Failed to create cash payment record');

      // Update appointment payment status
      const { error: updateError } = await supabase
        .from('appointments')
        .update({ payment_status: 'paid' })
        .eq('id', appointment.id);

      if (updateError) throw new Error(updateError.message || 'Failed to update appointment status');

      // Show signature pad
      if (paymentRecord) setPaymentRecordId(paymentRecord.id);
      setShowSignaturePad(true);
    } catch (error: any) {
      setCashPaymentError(error.message || 'Failed to process cash payment');
    } finally {
      setIsProcessingCashPayment(false);
    }
  };

  const handleApplyCredit = async () => {
    if (!appointment || !appointment.id || !clientId || !memberCreditAvailable) return;

    setIsApplyingCredit(true);
    setCreditError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(edgeFunctionUrl(EdgeFunctions.USE_WALLET_PAYMENT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          appointment_id: appointment.id,
          amount: 0,
          is_credit_only: true,
          client_id: clientId,
          credits_to_use: [{
            service_id: memberCreditAvailable.serviceId,
            quantity: 1,
          }],
          credit_savings: memberCreditAvailable.creditValue,
          original_amount: appointment.totalAmount ?? memberCreditAvailable.creditValue,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to apply member credit');
      }

      // Update local state
      setLocalPaymentStatus('paid');
      setMemberCreditAvailable(null);

      // Re-fetch payment record for signature
      const { data: paymentRecord } = await supabase
        .from('payments')
        .select('id')
        .eq('appointment_id', appointment.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (paymentRecord) setPaymentRecordId(paymentRecord.id);
      setShowSignaturePad(true);

      // Notify parent to refresh
      onPaymentComplete?.();
    } catch (error: any) {
      setCreditError(error.message || 'Failed to apply member credit');
    } finally {
      setIsApplyingCredit(false);
    }
  };

  const handleSubmitSignature = async () => {
    if (!signatureDataUrl || !appointment?.id || !paymentRecordId) return;
    setIsSubmittingSignature(true);
    setSignatureError(null);

    try {
      // 1. Convert base64 to binary
      const base64Data = signatureDataUrl.replace(/^data:image\/png;base64,/, '');
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // 2. Upload to signatures bucket
      const fileName = `payments/${appointment.id}_${Date.now()}.png`;
      const { error: uploadError } = await supabase.storage
        .from('signatures')
        .upload(fileName, bytes, { contentType: 'image/png', upsert: false });
      if (uploadError) throw new Error('Failed to upload signature');

      // 3. Get public URL
      const { data: urlData } = supabase.storage
        .from('signatures')
        .getPublicUrl(fileName);
      if (!urlData?.publicUrl) throw new Error('Failed to get signature URL');

      // 4. Update payment record
      const signedAt = new Date().toISOString();
      const { error: updateError } = await supabase
        .from('payments')
        .update({ signature_url: urlData.publicUrl, signed_at: signedAt })
        .eq('id', paymentRecordId);
      if (updateError) throw new Error('Failed to save signature');

      // 5. Show locked signature briefly, then close
      setExistingSignatureUrl(urlData.publicUrl);
      setExistingSignedAt(signedAt);
      setShowSignaturePad(false);

      setTimeout(() => {
        setLocalPaymentStatus('paid');
        setRefreshCounter(c => c + 1);
        onPaymentComplete?.();
      }, 2000);
    } catch (error: any) {
      setSignatureError(error.message || 'Failed to submit signature');
    } finally {
      setIsSubmittingSignature(false);
    }
  };

  const handleSkipSignature = () => {
    setShowSignaturePad(false);
    setSignatureDataUrl(null);
    setLocalPaymentStatus('paid');
    setRefreshCounter(c => c + 1);
    onPaymentComplete?.();
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to cancel this appointment?')) {
      if (onDelete && appointment.id) {
        await onDelete(appointment.id);
        onClose();
      }
    }
  };

  const handleTipSuccess = (tipData: { amount: number; tip_percentage: number | null }) => {
    // Update tip state immediately with the data from TipModal
    setExistingTip(tipData);
  };

  // Handle price adjustment
  const handleApplyAdjustment = async (
    adjustedTotal: number,
    discountPercent: number | null,
    reason: string
  ) => {
    if (!appointment || !appointment.id) return;

    setIsApplyingAdjustment(true);

    try {
      const userRole = isAdmin ? 'admin' : 'crew';
      const originalTotal = appointment.totalAmount || 0;

      // Call the RPC function to apply the adjustment
      const { data, error } = await supabase.rpc('apply_price_adjustment', {
        p_appointment_id: appointment.id,
        p_original_total: originalTotal,
        p_adjusted_total: adjustedTotal,
        p_discount_percent: discountPercent,
        p_reason: reason,
        p_user_role: userRole,
      });

      if (error) {
        throw new Error(error.message);
      }

      // Build the full priceAdjustment object to pass to parent
      const priceAdjustment: PriceAdjustment = {
        id: data || '', // RPC returns the adjustment ID
        appointment_id: appointment.id as string,
        original_total: originalTotal,
        adjusted_total: adjustedTotal,
        discount_percent: discountPercent,
        adjustment_reason: reason,
        adjusted_by: userProfile?.id || '',
        adjusted_by_role: userRole,
        created_at: new Date().toISOString(),
      };

      // Notify parent component with full adjustment data
      if (onPriceAdjustment) {
        onPriceAdjustment(appointment.id as string, adjustedTotal, priceAdjustment);
      }
    } catch (err: any) {
      console.error('Error applying price adjustment:', err);
      throw err;
    } finally {
      setIsApplyingAdjustment(false);
    }
  };

  const getPaymentStatusBadge = (status?: string) => {
    switch (status) {
      case 'paid':
        return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">Paid</span>;
      case 'pending_payment':
      case 'pending': // Handle legacy 'pending' value
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">Pending Payment</span>;
      case 'failed':
        return <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded">Failed</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded">Unknown</span>;
    }
  };

  const getSourceBadge = (source?: string) => {
    switch (source) {
      case 'online':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">Online Booking</span>;
      case 'admin':
        return <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded">Admin Created</span>;
      case 'phone':
        return <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded">Phone Booking</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded">Unknown</span>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Appointment Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            &times;
          </button>
        </div>

        <div className="px-6 py-4 space-y-6">
          {/* Client Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-3">Client Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex">
                <span className="w-32 text-gray-600 font-medium">Name:</span>
                <span className="text-gray-900">{appointment.client || 'N/A'}</span>
              </div>
              {appointment.clientEmail && (
                <div className="flex">
                  <span className="w-32 text-gray-600 font-medium">Email:</span>
                  <span className="text-gray-900">{appointment.clientEmail}</span>
                </div>
              )}
              {appointment.clientPhone && (
                <div className="flex">
                  <span className="w-32 text-gray-600 font-medium">Phone:</span>
                  <span className="text-gray-900">{appointment.clientPhone}</span>
                </div>
              )}
              {/* Consent Form Status */}
              {consentFormComplete !== null && (
                <div className="flex items-center">
                  <span className="w-32 text-gray-600 font-medium">Consent Form:</span>
                  <div className="flex items-center gap-2">
                    <FormStatusBadge isComplete={consentFormComplete} />
                    {!consentFormComplete && consentFormToken && (
                      <button
                        onClick={() => {
                          const formUrl = `${window.location.origin}/consent-form?token=${consentFormToken}`;
                          navigator.clipboard.writeText(formUrl);
                          alert('Form link copied to clipboard!');
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        Copy Link
                      </button>
                    )}
                    {consentFormComplete && consentFormToken && (
                      <button
                        onClick={() => {
                          window.open(`/consent-form?token=${consentFormToken}`, '_blank');
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        View Form
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-3">Services</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              {appointment.services && appointment.services.length > 0 ? (
                <div className="space-y-3">
                  {appointment.services.map((service, index) => (
                    <div key={index} className="flex justify-between items-center pb-3 border-b border-gray-200 last:border-b-0 last:pb-0">
                      <div>
                        <div className="font-medium text-gray-900">{service.name}</div>
                        <div className="text-sm text-gray-500">{service.duration} minutes</div>
                      </div>
                      <div className="text-gray-900 font-semibold">
                        ${service.price.toFixed(2)}
                      </div>
                    </div>
                  ))}
                  {/* Subtotal */}
                  <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300">
                    <div className="font-semibold text-gray-900">
                      {(paymentDiscountInfo?.creditSavings || paymentDiscountInfo?.memberDiscountAmount || appointment.adjustedTotal != null) ? 'Subtotal' : 'Total'}
                    </div>
                    <div className={`text-lg font-bold ${(paymentDiscountInfo?.creditSavings || paymentDiscountInfo?.memberDiscountAmount || appointment.adjustedTotal != null) ? 'text-gray-500' : 'text-gray-900'}`}>
                      ${(appointment.totalAmount || 0).toFixed(2)}
                    </div>
                  </div>

                  {/* Credit Applied (service credits used) */}
                  {paymentDiscountInfo?.creditSavings != null && paymentDiscountInfo.creditSavings > 0 && (
                    <div className="flex justify-between items-center">
                      <div className="text-purple-700">Credit Applied</div>
                      <div className="text-purple-700 font-medium">
                        -${paymentDiscountInfo.creditSavings.toFixed(2)}
                      </div>
                    </div>
                  )}

                  {/* Member Discount */}
                  {paymentDiscountInfo?.memberDiscountAmount != null && paymentDiscountInfo.memberDiscountAmount > 0 && (
                    <div className="flex justify-between items-center">
                      <div className="text-green-700">Member Discount ({paymentDiscountInfo.memberDiscountPercent ?? 20}%)</div>
                      <div className="text-green-700 font-medium">
                        -${paymentDiscountInfo.memberDiscountAmount.toFixed(2)}
                      </div>
                    </div>
                  )}

                  {/* Pay Now Discount */}
                  {paymentDiscountInfo?.payNowDiscountAmount != null && paymentDiscountInfo.payNowDiscountAmount > 0 && (
                    <div className="flex justify-between items-center">
                      <div className="text-green-700">Pay Now Discount (5%)</div>
                      <div className="text-green-700 font-medium">
                        -${paymentDiscountInfo.payNowDiscountAmount.toFixed(2)}
                      </div>
                    </div>
                  )}

                  {/* Adjusted Total (manual price adjustment) */}
                  {appointment.adjustedTotal != null && (
                    <div className="flex justify-between items-center">
                      <div className="text-green-700">Manual Adjustment</div>
                      <div className="text-green-700 font-medium">
                        → ${appointment.adjustedTotal.toFixed(2)}
                      </div>
                    </div>
                  )}

                  {/* Final Total - show when there are discounts/credits */}
                  {(paymentDiscountInfo?.creditSavings || paymentDiscountInfo?.memberDiscountAmount || paymentDiscountInfo?.payNowDiscountAmount || appointment.adjustedTotal != null) && (
                    <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                      <div className="font-bold text-gray-900">
                        {paymentDiscountInfo?.paymentType === 'pay_at_spa' ? 'Total Due' : 'Total'}
                      </div>
                      <div className={`text-lg font-bold ${paymentDiscountInfo?.paymentType === 'pay_at_spa' ? 'text-orange-600' : 'text-green-700'}`}>
                        ${(paymentDiscountInfo?.actualPaidAmount ?? appointment.adjustedTotal ?? appointment.totalAmount ?? 0).toFixed(2)}
                      </div>
                    </div>
                  )}

                  {/* Price Adjustment Reason (if exists) */}
                  {appointment.priceAdjustment && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                      <div className="text-green-800">
                        <span className="font-medium">Adjustment:</span>{' '}
                        {appointment.priceAdjustment.discount_percent != null
                          ? `${appointment.priceAdjustment.discount_percent}% discount`
                          : 'Custom amount'}
                      </div>
                      <div className="text-green-700 mt-1">
                        <span className="font-medium">Reason:</span> {appointment.priceAdjustment.adjustment_reason}
                      </div>
                      <div className="text-green-600 text-xs mt-1">
                        By {appointment.priceAdjustment.adjusted_by_role} on{' '}
                        {new Date(appointment.priceAdjustment.created_at).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-gray-500">No services listed</div>
              )}

              {/* Price Adjustment Section - Only for unpaid appointments */}
              {(isAdmin || isCrew) && effectivePaymentStatus !== 'paid' && (
                <PriceAdjustmentSection
                  originalTotal={appointment.totalAmount || 0}
                  currentAdjustment={appointment.priceAdjustment}
                  userRole={isAdmin ? 'admin' : 'crew'}
                  maxDiscountPercent={maxDiscountPercent}
                  onApplyAdjustment={handleApplyAdjustment}
                  isSubmitting={isApplyingAdjustment}
                />
              )}
            </div>
          </div>

          {/* Appointment Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-3">Appointment Details</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex">
                <span className="w-32 text-gray-600 font-medium">Date:</span>
                <span className="text-gray-900">{format(new Date(appointment.start), 'EEEE, MMMM d, yyyy')}</span>
              </div>
              <div className="flex">
                <span className="w-32 text-gray-600 font-medium">Time:</span>
                <span className="text-gray-900">
                  {format(new Date(appointment.start), 'h:mm a')} - {format(new Date(appointment.end), 'h:mm a')}
                </span>
              </div>
              <div className="flex">
                <span className="w-32 text-gray-600 font-medium">Status:</span>
                <span className={`px-2 py-1 text-xs font-semibold rounded ${
                  appointment.status === 'scheduled' ? 'bg-green-100 text-green-800' :
                  appointment.status === 'pending_payment' ? 'bg-yellow-100 text-yellow-800' :
                  appointment.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                  appointment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  appointment.status === 'no_show' ? 'bg-gray-100 text-gray-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {appointment.status?.replace('_', ' ').split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-3">Payment Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex">
                <span className="w-32 text-gray-600 font-medium">Payment Status:</span>
                {getPaymentStatusBadge(effectivePaymentStatus)}
              </div>
              {/* Subtotal (original price before discounts) */}
              <div className="flex">
                <span className="w-32 text-gray-600 font-medium">Subtotal:</span>
                <span className="text-gray-900">
                  ${(paymentDiscountInfo?.originalAmount ?? appointment.totalAmount ?? 0).toFixed(2)}
                </span>
              </div>

              {/* Pay-now discount if applied (5% for non-members) */}
              {paymentDiscountInfo?.payNowDiscountAmount != null && paymentDiscountInfo.payNowDiscountAmount > 0 && (
                <div className="flex">
                  <span className="w-32 text-gray-600 font-medium">Pay Now Discount:</span>
                  <span className="text-green-600">
                    -5% (-${paymentDiscountInfo.payNowDiscountAmount.toFixed(2)})
                  </span>
                </div>
              )}

              {/* Member discount if applied */}
              {paymentDiscountInfo?.memberDiscountAmount != null && paymentDiscountInfo.memberDiscountAmount > 0 && (
                <div className="flex">
                  <span className="w-32 text-gray-600 font-medium">Member Discount:</span>
                  <span className="text-green-600">
                    -{paymentDiscountInfo.memberDiscountPercent ?? 20}% (-${paymentDiscountInfo.memberDiscountAmount.toFixed(2)})
                  </span>
                </div>
              )}

              {/* Credit savings if applied (service credits used) */}
              {paymentDiscountInfo?.creditSavings != null && paymentDiscountInfo.creditSavings > 0 && (
                <div className="flex">
                  <span className="w-32 text-gray-600 font-medium">Credit Applied:</span>
                  <span className="text-purple-600">
                    -${paymentDiscountInfo.creditSavings.toFixed(2)}
                  </span>
                </div>
              )}

              {/* Manual adjustment if applied */}
              {appointment.adjustedTotal != null && (
                <div className="flex">
                  <span className="w-32 text-gray-600 font-medium">Manual Adjustment:</span>
                  <span className="text-green-600">
                    Adjusted to ${appointment.adjustedTotal.toFixed(2)}
                  </span>
                </div>
              )}

              {/* Final paid/due amount */}
              <div className="flex border-t border-gray-200 pt-2 mt-2">
                <span className="w-32 text-gray-600 font-medium">
                  {paymentDiscountInfo?.paymentType === 'pay_at_spa' ? 'Amount Due:' : 'Amount Paid:'}
                </span>
                <span className={`font-semibold ${paymentDiscountInfo?.paymentType === 'pay_at_spa' ? 'text-orange-600' : 'text-gray-900'}`}>
                  ${(paymentDiscountInfo?.actualPaidAmount ?? appointment.adjustedTotal ?? appointment.totalAmount ?? 0).toFixed(2)}
                  {paymentDiscountInfo?.paymentType === 'pay_at_spa' && (
                    <span className="ml-2 text-xs font-normal text-orange-500">(at spa)</span>
                  )}
                </span>
              </div>

              {/* Tip Display - Show if tip exists */}
              {existingTip && (
                <div className="flex items-center">
                  <span className="w-32 text-gray-600 font-medium">Tip:</span>
                  <span className="text-green-700 font-semibold">
                    ${existingTip.amount.toFixed(2)}
                    {existingTip.tip_percentage && (
                      <span className="text-sm text-gray-500 ml-1">({existingTip.tip_percentage}%)</span>
                    )}
                  </span>
                </div>
              )}

              {/* Add Tip Button - Show when paid and no existing tip */}
              {effectivePaymentStatus === 'paid' && !existingTip && !isFetchingTip && (
                <div className="pt-2 border-t border-gray-200">
                  <button
                    onClick={() => setIsTipModalOpen(true)}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <span>Add Tip</span>
                  </button>
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    Add a tip for the service provider
                  </p>
                </div>
              )}

              {/* Payment Buttons - Only show if payment is not paid */}
              {effectivePaymentStatus !== 'paid' && (
                <div className="pt-2 border-t border-gray-200 space-y-3">
                  {/* Member Credit Available Banner */}
                  {memberCreditAvailable && (
                    <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-teal-700 font-semibold">Member Credit Available</span>
                      </div>
                      <p className="text-sm text-teal-600 mb-3">
                        This service is covered by a free member credit ({memberCreditAvailable.quantity} remaining)
                      </p>
                      {creditError && (
                        <p className="text-sm text-red-600 mb-2">{creditError}</p>
                      )}
                      <button
                        onClick={handleApplyCredit}
                        disabled={isApplyingCredit}
                        className="w-full px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isApplyingCredit ? (
                          <>
                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Applying Credit...
                          </>
                        ) : (
                          <>Apply Member Credit (Free)</>
                        )}
                      </button>
                    </div>
                  )}

                  {/* OR Divider after credit section */}
                  {memberCreditAvailable && (
                    <div className="flex items-center">
                      <div className="flex-1 border-t border-gray-300"></div>
                      <span className="px-3 text-sm text-gray-500">OR</span>
                      <div className="flex-1 border-t border-gray-300"></div>
                    </div>
                  )}

                  {/* Terminal Status Display */}
                  {terminalStatus && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm text-blue-800 text-center">{terminalStatus}</p>
                    </div>
                  )}

                  {/* Terminal Error Display */}
                  {terminalError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-800">{terminalError}</p>
                    </div>
                  )}

                  {/* Payment Link Button */}
                  <div>
                    <button
                      onClick={handleCreatePaymentLink}
                      disabled={isCreatingPayment || isProcessingTerminal}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isCreatingPayment ? 'Creating Payment Link...' : 'Open Payment Page'}
                    </button>
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      Opens Stripe checkout for in-person contactless payment
                    </p>
                    {paymentError && (
                      <p className="text-xs text-red-600 mt-2">{paymentError}</p>
                    )}
                  </div>

                  {/* OR Divider */}
                  <div className="flex items-center">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="px-3 text-sm text-gray-500">OR</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>

                  {/* Pay with Wallet Button */}
                  {clientId && clientWalletBalance !== null && (
                    <div>
                      {walletPaymentError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-md mb-2">
                          <p className="text-sm text-red-800">{walletPaymentError}</p>
                        </div>
                      )}
                      <button
                        onClick={handleWalletPayment}
                        disabled={
                          isProcessingWalletPayment ||
                          isCreatingPayment ||
                          isProcessingTerminal ||
                          clientWalletBalance < (appointment.adjustedTotal ?? appointment.totalAmount ?? 0)
                        }
                        className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isProcessingWalletPayment ? (
                          <>
                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Processing...
                          </>
                        ) : (
                          <>Pay with Wallet (${(appointment.adjustedTotal ?? appointment.totalAmount ?? 0).toFixed(2)})</>
                        )}
                      </button>
                      <p className="text-xs text-gray-500 mt-1 text-center">
                        {clientWalletBalance >= (appointment.adjustedTotal ?? appointment.totalAmount ?? 0)
                          ? `Client wallet balance: $${clientWalletBalance.toFixed(2)}`
                          : `Insufficient wallet balance: $${clientWalletBalance.toFixed(2)}`}
                      </p>
                    </div>
                  )}

                  {/* OR Divider */}
                  <div className="flex items-center">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="px-3 text-sm text-gray-500">OR</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>

                  {/* Process on Reader Button */}
                  <div>
                    {canCancelPayment ? (
                      <button
                        onClick={handleCancelPayment}
                        className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                      >
                        ✕ Cancel Payment
                      </button>
                    ) : (
                      <button
                        onClick={handleProcessOnReader}
                        disabled={isProcessingTerminal || isCreatingPayment}
                        className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isProcessingTerminal ? (
                          <>
                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Processing...
                          </>
                        ) : (
                          <>
                            💳 Process Payment on Reader
                          </>
                        )}
                      </button>
                    )}
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      {canCancelPayment
                        ? 'Click cancel to stop the payment process'
                        : 'Use Stripe S700 card reader for in-person payment'}
                    </p>
                  </div>

                  {/* OR Divider */}
                  <div className="flex items-center">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="px-3 text-sm text-gray-500">OR</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>

                  {/* Pay as Cash Button */}
                  <div>
                    <button
                      onClick={handleCashPayment}
                      disabled={isProcessingCashPayment || isCreatingPayment || isProcessingTerminal || isProcessingWalletPayment}
                      className="w-full px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isProcessingCashPayment ? (
                        <>
                          <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                          Processing...
                        </>
                      ) : (
                        <>{'💵 Pay as Cash ($' + (appointment.adjustedTotal ?? appointment.totalAmount ?? 0).toFixed(2) + ')'}</>
                      )}
                    </button>
                    {cashPaymentError && (
                      <p className="text-xs text-red-600 mt-1 text-center">{cashPaymentError}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      Record a cash payment for this appointment
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payment Signature */}
          {(showSignaturePad || existingSignatureUrl || (effectivePaymentStatus === 'paid' && !existingSignatureUrl && paymentRecordId)) && (
            <div ref={signatureSectionRef}>
              <h3 className="text-lg font-medium text-gray-800 mb-3">Payment Signature</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {existingSignatureUrl ? (
                  // Signed state - show locked signature with timestamp
                  <>
                    <SignatureCanvas
                      onSignatureChange={() => {}}
                      initialSignature={existingSignatureUrl}
                      disabled={true}
                    />
                    {existingSignedAt && (
                      <p className="text-sm text-green-700 text-center">
                        Signed on {format(new Date(existingSignedAt), 'MMM d, yyyy h:mm a')}
                      </p>
                    )}
                  </>
                ) : (
                  // Collecting state - show signature pad with buttons
                  <>
                    <p className="text-sm text-gray-600 text-center mb-2">
                      Please sign below to confirm payment
                    </p>
                    <SignatureCanvas
                      onSignatureChange={(dataUrl) => setSignatureDataUrl(dataUrl)}
                    />
                    {signatureError && (
                      <p className="text-sm text-red-600 text-center">{signatureError}</p>
                    )}
                    <div className="flex gap-3">
                      <button
                        onClick={handleSkipSignature}
                        disabled={isSubmittingSignature}
                        className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50"
                      >
                        Skip
                      </button>
                      <button
                        onClick={handleSubmitSignature}
                        disabled={!signatureDataUrl || isSubmittingSignature}
                        className="flex-1 px-4 py-2 bg-[#3A5E63] text-white rounded-md hover:bg-[#2d484c] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isSubmittingSignature ? (
                          <>
                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Saving...
                          </>
                        ) : (
                          'Save Signature'
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Booking Source */}
          {appointment.appointmentSource && (
            <div>
              <h3 className="text-lg font-medium text-gray-800 mb-3">Booking Information</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex">
                  <span className="w-32 text-gray-600 font-medium">Source:</span>
                  {getSourceBadge(appointment.appointmentSource)}
                </div>
              </div>
            </div>
          )}

          {/* Customer Notes - Read-only */}
          {appointment.notes && (
            <div>
              <h3 className="text-lg font-medium text-gray-800 mb-3">Customer Notes</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-900 whitespace-pre-wrap">{appointment.notes}</p>
              </div>
            </div>
          )}

          {/* Crew Notes - Editable, Internal Only */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-medium text-gray-800">
                Crew Notes
                <span className="ml-2 text-xs font-normal text-gray-500">(internal only)</span>
              </h3>
              {!isEditingCrewNotes && (isAdmin || isCrew) && (
                <button
                  onClick={() => setIsEditingCrewNotes(true)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {appointment.crewNotes ? 'Edit' : 'Add Notes'}
                </button>
              )}
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              {isEditingCrewNotes ? (
                <div className="space-y-3">
                  <textarea
                    value={crewNotesValue}
                    onChange={(e) => setCrewNotesValue(e.target.value)}
                    placeholder="Add internal notes about this appointment..."
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none bg-white"
                    rows={4}
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={handleCancelCrewNotesEdit}
                      disabled={isSavingCrewNotes}
                      className="px-3 py-1.5 text-sm text-gray-600 bg-gray-200 rounded hover:bg-gray-300 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveCrewNotes}
                      disabled={isSavingCrewNotes}
                      className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                    >
                      {isSavingCrewNotes ? (
                        <>
                          <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                          Saving...
                        </>
                      ) : (
                        'Save Notes'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-900 whitespace-pre-wrap">
                  {appointment.crewNotes || <span className="text-gray-400 italic">No crew notes added</span>}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-between">
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Cancel Appointment
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
            {onEdit && appointment?.paymentStatus !== 'paid' && (
              <button
                onClick={onEdit}
                className="px-4 py-2 bg-[#3A5E63] text-white rounded hover:bg-[#2d484c] transition-colors"
              >
                Rebook Appointment
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tip Modal */}
      {appointment && (
        <TipModal
          isOpen={isTipModalOpen}
          onClose={() => setIsTipModalOpen(false)}
          appointmentId={appointment.id as string}
          serviceTotal={paymentDiscountInfo?.originalAmount || appointment.totalAmount || 0}
          staffName={'Staff Member'}
          onSuccess={handleTipSuccess}
          paymentMethodId={paymentMethodId}
          paymentSource={paymentSource}
          clientWalletBalance={clientWalletBalance}
          clientHasSavedCard={clientHasSavedCard}
        />
      )}
    </div>
  );
};

export default AppointmentDetailsModal;
